import { type FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import cx from "classnames";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Photo, Video } from "../../types";

import styles from "./PhotoViewerDesktopCaption.module.css";
import markdownStyles from "../AssistantMessage/MarkdownBlock.module.css";

const remarkPlugins = [remarkGfm];

/** Outer max-height in collapsed state (px). */
const COLLAPSED_MAX_HEIGHT = 140;
/** Outer vertical padding (top + bottom) in the BASE state (no
    chevron). Keep in sync with `.root` `padding: 16px` in module.css. */
const OUTER_VERTICAL_PADDING_BASE = 32;
/** Vertical padding when the chevron expand button is present — the
    chevron's pill wrapper (56 × 56) hangs over the top edge of the
    caption block by ~28 px. We bump `padding-top` to 32 px so the
    first text line clears the chevron with breathing room. Keep in
    sync with `.root.withChevron` rule in module.css. */
const OUTER_VERTICAL_PADDING_WITH_CHEVRON = 48;
/** Text area inside the collapsed outer when the chevron is present
    (the tighter of the two possible content areas). Using the smaller
    threshold avoids the chicken-and-egg: if content fits in 108 px
    (base) but not in 92 px (with chevron), turning on the chevron
    would push the content into the bottom padding band. We pre-empt
    that by gating `canExpand` on the post-chevron area. */
const COLLAPSED_CONTENT_AREA = COLLAPSED_MAX_HEIGHT - OUTER_VERTICAL_PADDING_WITH_CHEVRON;
/** Must match `transition: height ...` in module.css. */
const COLLAPSE_TRANSITION_MS = 320;

type Props = {
  item: Photo | Video;
  expanded: boolean;
  /**
   * Fired whenever the caption's intrinsic content height crosses the
   * `COLLAPSED_MAX_HEIGHT` threshold. `true` ⇒ caption has content that
   * would be hidden in collapsed state; parent should render the expand
   * chevron. `false` ⇒ content fits entirely.
   */
  onCanExpandChange: (canExpand: boolean) => void;
};

/**
 * Desktop variant of `PhotoViewerCaption`. Controlled max-height — parent
 * owns the `expanded` state (so the chevron button can live above the
 * caption block, between photo and caption per Figma). Reports overflow
 * status via `onCanExpandChange` so the parent can hide the chevron
 * when content fits.
 *
 * Animates `max-height` via inline style (target = measured content
 * height when expanded, `COLLAPSED_MAX_HEIGHT` otherwise). Animating
 * to `auto` is illegal in CSS; we measure with ResizeObserver and write
 * a pixel target instead. Both sides of the toggle animate symmetrically.
 *
 * Mask gradient at the bottom is shown only when there is hidden
 * content (collapsed + overflow). Removed on expanded so trailing text
 * doesn't fade out.
 */
export const PhotoViewerDesktopCaption: FC<Props> = ({
  item,
  expanded,
  onCanExpandChange,
}) => {
  const outerRef = useRef<HTMLDivElement>(null);
  const scrollViewportRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);
  // Apply line-clamp with a delay on collapse so the inner doesn't
  // jump to its clamped intrinsic height (~115px) at the start of the
  // animation while `max-height` is still transitioning from a much
  // larger value. The outer follows the smaller of the two limits;
  // shrinking the inner mid-transition collapses the box instantly
  // and the max-height animation no longer has anything to drive.
  // On expand we drop the clamp synchronously so the inner can grow
  // immediately while `max-height` interpolates upward.
  const [clampActive, setClampActive] = useState(!expanded);

  const title = item.title ?? item.caption;
  const description = item.title ? item.description : undefined;

  // Measure intrinsic content height — needed both for the animation
  // target (when expanded) AND for the overflow gate (whether to show
  // the chevron). ResizeObserver fires on font load, viewport resize,
  // and any layout shift inside the markdown body.
  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const measure = () => {
      // `scrollHeight` excludes margins of the first/last child but
      // matches the box we're animating to — fine for our purposes.
      setContentHeight(el.scrollHeight);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [title, description]);

  // Push overflow status up so the parent can hide the chevron when
  // content fits within the content-area (= outer max-height minus
  // its vertical padding). Comparing against the outer max-height
  // would silently mis-classify content between `CONTENT_AREA` and
  // `MAX_HEIGHT` — that content overflows visually into the bottom
  // padding band, eating the breathing room.
  useEffect(() => {
    onCanExpandChange(contentHeight > COLLAPSED_CONTENT_AREA + 1);
  }, [contentHeight, onCanExpandChange]);

  // Async clamp toggle for smooth animation in both directions.
  // Expand → drop clamp immediately. Collapse → defer clamp until
  // the max-height transition finishes (see CSS).
  useEffect(() => {
    if (expanded) {
      setClampActive(false);
      return;
    }
    const timer = window.setTimeout(() => {
      setClampActive(true);
    }, COLLAPSE_TRANSITION_MS);
    return () => window.clearTimeout(timer);
  }, [expanded]);

  // Scroll-fade hints — drive the top/bottom mask via direct classList
  // toggles instead of React state. The scroll handler fires on every
  // wheel/touch tick; routing it through `setState` would trigger a
  // full re-render per frame and visibly judder the scroll. classList
  // toggles are atomic DOM mutations and never schedule React work.
  // Re-applied from `useLayoutEffect` after every render so the
  // declarative `className` doesn't wipe our mutations.
  //
  // Masks are gated on `expanded`: in collapsed mode the line-clamp
  // ellipsis already signals "more below", scroll is disabled
  // (overflow-y: hidden), and a fade would be cosmetic-only. Without
  // a chevron (canExpand = false) `expanded` can never become true,
  // so this single guard also covers the no-chevron case.
  const updateScrollMask = useCallback(() => {
    const el = scrollViewportRef.current;
    if (!el) return;
    if (!expanded) {
      el.classList.remove(styles.maskTop, styles.maskBottom, styles.maskBoth);
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = el;
    const overflowing = scrollHeight > clientHeight + 1;
    const atTop = scrollTop <= 2;
    const atBottom = scrollTop + clientHeight >= scrollHeight - 2;
    el.classList.toggle(styles.maskTop, overflowing && !atTop && atBottom);
    el.classList.toggle(styles.maskBottom, overflowing && atTop && !atBottom);
    el.classList.toggle(styles.maskBoth, overflowing && !atTop && !atBottom);
  }, [expanded]);

  useLayoutEffect(() => {
    updateScrollMask();
  }, [contentHeight, expanded, clampActive, updateScrollMask]);

  if (!title && !description) return null;

  // Explicit `height` transition (not `max-height`) — the outer is
  // shielded from inner intrinsic changes during the animation, so the
  // class flip from `.innerCollapsed` (inner.intrinsic ~68 px) to
  // flex (inner.intrinsic = full) at the end of a collapse no longer
  // snaps the outer. The CSS rule animates `height`; we drive the
  // target via inline style.
  //
  // `contentHeight` IS `inner.scrollHeight` (no inner padding —
  // padding lives on outer for static-frame behaviour). We add the
  // outer padding back when computing the outer height so the frame
  // stays visible. Padding is larger when chevron is present (extra
  // top room so the first line clears it).
  //
  // Collapsed + overflow → fixed 140 px (consistent visual + outer
  //   padding-bottom visible below the clamped text).
  // Collapsed + fits → auto-fit (content + outer padding) so short
  //   captions aren't stretched with empty space.
  // Expanded → min(content + padding, viewport/3) — never taller than
  //   one third of the viewport so the photo above keeps the dominant
  //   role. When content exceeds the ceiling the inner scroll (hidden
  //   scrollbar, see module.css) lets the user reach the tail, and
  //   the outer padding stays as a static frame around the viewport.
  const canExpand = contentHeight > COLLAPSED_CONTENT_AREA + 1;
  const outerVerticalPadding = canExpand
    ? OUTER_VERTICAL_PADDING_WITH_CHEVRON
    : OUTER_VERTICAL_PADDING_BASE;
  let heightStyle: string;
  if (expanded) {
    heightStyle = `min(${contentHeight + outerVerticalPadding}px, calc(100vh / 3))`;
  } else if (canExpand) {
    heightStyle = `${COLLAPSED_MAX_HEIGHT}px`;
  } else {
    heightStyle = `${contentHeight + outerVerticalPadding}px`;
  }
  // `clampActive` is still deferred so the line-clamp / ellipsis don't
  // pop in at the moment of click — outer height transition carries
  // the visual; the actual text reflow lands at the end of the
  // animation when the box has reached its collapsed footprint.
  const showClamp = canExpand && !expanded && clampActive;

  const isTitleOnly = Boolean(title) && !description;

  return (
    <div
      ref={outerRef}
      className={cx(
        styles.root,
        expanded && styles.expanded,
        canExpand && styles.withChevron,
        isTitleOnly && styles.titleOnly,
      )}
      style={{ height: heightStyle }}
    >
      <div
        ref={scrollViewportRef}
        className={styles.scrollViewport}
        onScroll={updateScrollMask}
      >
        <div
          ref={innerRef}
          className={cx(styles.inner, showClamp && styles.innerCollapsed)}
        >
          {title && <h1 className={styles.title}>{title}</h1>}
          {description && (
            <div className={cx(styles.description, markdownStyles.markdown)}>
              <Markdown remarkPlugins={remarkPlugins}>{description}</Markdown>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
